import React, { useEffect, useState } from 'react';
import { useScenario } from '../context/ScenarioContext';
import { useAuth } from '../context/AuthContext';
import { api, money, moneyFull, pct } from '../lib/api';
import { KpiCard, SectionHeader, Callout } from '../components/UI';
import { ClockLabel } from '../components/sales/ClockLabel';
import { DataSourceBadge } from '../components/sales/DataSourceBadge';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import { ArrowUpRight, ArrowDownRight, TrendingUp, TrendingDown, Printer, RefreshCw, AlertTriangle, CheckCircle2, Clock, Target } from 'lucide-react';
import CashRunwayCard from './CashRunwayCard';
import IoltaCard from './IoltaCard';
import ReconcileBalancesModal from './ReconcileBalancesModal';

// Hoisted chart styles — identical references across renders keep Recharts happy.
const CHART_GRID_STROKE = { strokeDasharray: '2 4', stroke: '#141B4D', strokeOpacity: 0.1 };
const CHART_AXIS_LINE = { stroke: '#141B4D', strokeOpacity: 0.3 };
const CHART_TOOLTIP_STYLE = { background: '#FFFFFF', border: '1px solid #141B4D', color: '#141B4D', borderRadius: 0, boxShadow: '0 4px 12px rgba(20,27,77,0.12)', fontSize: 12 };
const CHART_TOOLTIP_LABEL = { color: '#141B4D', fontWeight: 600, marginBottom: 4 };
const CHART_TOOLTIP_ITEM = { color: '#141B4D' };
const CHART_LEGEND_WRAPPER = { fontSize: 12, paddingTop: 8 };
const CHART_MARGIN = { left: 10, right: 10, top: 10, bottom: 10 };
const CHART_DOT_NAVY = { r: 3, fill: '#141B4D' };
const CHART_DOT_COBALT = { r: 3, fill: '#1D57A5' };
const SAFETY_FLOOR_LABEL = { value: 'Safety floor $200K', position: 'insideTopRight', fill: '#141B4D', fontSize: 11 };

export default function Dashboard() {
  const { active, forecastData } = useScenario();
  const { user } = useAuth();
  const [bhag, setBhag] = useState(null);
  const [actuals, setActuals] = useState([]);
  const [pulse, setPulse] = useState(null);
  const [sales, setSales] = useState(null);
  const [mtdSeries, setMtdSeries] = useState(null);
  const [period, setPeriod] = useState('fy');  // fy | ytd | qtd | mtd
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileTick, setReconcileTick] = useState(0);  // forces cards to reload

  useEffect(() => {
    Promise.all([
      api.get('/bhag').then((r) => setBhag(r.data)),
      api.get('/actuals').then((r) => setActuals(r.data)),
      api.get('/daily-pulse/today').then((r) => setPulse(r.data)).catch(() => {}),
      api.get('/sales').then((r) => setSales(r.data)).catch(() => {}),
      api.get('/sales/console/mtd').then((r) => setMtdSeries(r.data)).catch(() => {}),
    ]);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!active || !forecastData) {
    return <div className="label-editorial">Loading forecast…</div>;
  }

  const f = forecastData.forecast;
  const months = f.months;
  const monthlyBhag = bhag ? bhag.annual_revenue / 12 : 0;

  // Per-month BHAG targets from the sheet (Row 3 "Revenue Target - ADJUSTED").
  // Kat pointed out our chart was showing a flat annual/12 line, ignoring the
  // per-month ADJUSTED targets Joey maintains (e.g., June $250K, Dec $300K).
  const bhagByLabel = {};
  (bhag?.months || []).forEach((m) => {
    if (m?.label && typeof m.revenue_target === 'number') {
      // Match forecast labels like "Jan 2026" against sheet labels like "January".
      const short = m.label.slice(0, 3);
      bhagByLabel[m.label] = m.revenue_target;
      bhagByLabel[short] = m.revenue_target;
    }
  });
  const bhagForLabel = (label) => {
    if (!label) return monthlyBhag;
    if (bhagByLabel[label] != null) return bhagByLabel[label];
    const short = label.split(' ')[0];
    if (bhagByLabel[short] != null) return bhagByLabel[short];
    return monthlyBhag;
  };

  // Doctrine: actuals ALWAYS beat forecast for any month QBO has logged.
  // For the trend chart we mirror the Sales Plan visual grammar (blue
  // Adjusted-plan primary line, dashed navy Original baseline, teal solid
  // Actual for closed months only). This ensures Dashboard uses the same
  // approved chart language the team already reviews on Plan.
  // Chart shows current calendar year only (Jan-Dec) per user request.
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonthIdx = today.getMonth();   // 0-11
  const currentQuarter = Math.floor(currentMonthIdx / 3);  // 0-3
  const actualLabels = new Set(actuals.map((a) => a.month_label));

  // Sales-Plan-style row builder: 12 rows keyed by short month (Jan..Dec).
  // Adjusted plan = past actuals where they exist + future adjusted target.
  // Original plan = BHAG row 2 across all 12 months (baseline).
  // Actual = closed-month cash revenue only.
  const monthShort = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const bhagByMonthNum = {};
  (bhag?.months || []).forEach((m) => { if (m?.month) bhagByMonthNum[m.month] = m; });
  const actualsByMonthIdx = {};
  actuals
    .filter((a) => new Date(a.date_iso).getUTCFullYear() === currentYear)
    .forEach((a) => { actualsByMonthIdx[new Date(a.date_iso).getUTCMonth()] = a; });
  const forecastByLabel = {};
  months.forEach((m) => { forecastByLabel[m.label] = m; forecastByLabel[m.label.split(' ')[0]] = m; });

  const chartData = monthShort.map((short, i) => {
    const bm = bhagByMonthNum[i + 1] || {};
    const act = actualsByMonthIdx[i];
    const adjustedTarget = bm.revenue_target ?? forecastByLabel[short]?.income?.total ?? null;
    const originalTarget = bm.revenue_target_original ?? adjustedTarget;
    const actualVal = act ? act.revenue : (bm.revenue_actual ?? null);
    const isPast = actualVal != null;
    return {
      label: short,
      actual: isPast ? actualVal : null,
      adjusted: isPast ? actualVal : adjustedTarget,   // hybrid: past actuals + future adjusted
      original: originalTarget,
    };
  });

  // FY calendar-year totals: closed actuals + forecast months in the same year,
  // with NO overlap (actuals win where both exist).
  const fyActuals = actuals.filter((a) => new Date(a.date_iso).getUTCFullYear() === currentYear);
  const fyForecastMonths = months.filter(
    (m) => new Date(m.date_iso).getUTCFullYear() === currentYear && !actualLabels.has(m.label),
  );

  // Calendar-year quarterly roll-up (Jan-Mar, Apr-Jun, Jul-Sep, Oct-Dec of
  // currentYear). Actuals win where they exist; forecast fills the rest.
  // We deliberately don't rely on the backend `f.quarterly` here — that slice
  // is 12 months starting from the current month, so its Q1-Q4 labels don't
  // align to calendar quarters. Joey/Kat want the dashboard tied to the
  // current calendar year, so we compute it in-page.
  const calendarQuarterly = [0, 1, 2, 3].map((qIdx) => {
    const monthsInQ = [qIdx * 3, qIdx * 3 + 1, qIdx * 3 + 2];
    let rev = 0, exp = 0, net = 0;
    monthsInQ.forEach((mIdx) => {
      const act = fyActuals.find((a) => new Date(a.date_iso).getUTCMonth() === mIdx);
      if (act) {
        rev += act.revenue || 0;
        exp += act.expenses || 0;
        net += act.net_income || 0;
      } else {
        const fc = fyForecastMonths.find((m) => new Date(m.date_iso).getUTCMonth() === mIdx);
        if (fc) {
          rev += fc.income?.total || 0;
          exp += fc.expenses?.total || 0;
          net += fc.net_income || 0;
        }
      }
    });
    return {
      label: `Q${qIdx + 1}`,
      revenue: rev,
      expenses: exp,
      net_income: net,
      margin: rev ? net / rev : 0,
    };
  });

  // --- Window-based aggregation ---
  // Each window picks which months count and how to label the cards.
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const dayNum = today.getDate();
  const monthPct = dayNum / daysInMonth;
  const fyDayNum = Math.floor((today - new Date(today.getFullYear(), 0, 1)) / 86400000) + 1;
  const fyDaysTotal = ((today.getFullYear() % 4 === 0 && today.getFullYear() % 100 !== 0) || today.getFullYear() % 400 === 0) ? 366 : 365;
  const yearElapsedPct = fyDayNum / fyDaysTotal;

  const monthInWindow = (date, win) => {
    const d = new Date(date);
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    if (y !== currentYear) return false;
    if (win === 'fy') return true;
    if (win === 'ytd') return m <= currentMonthIdx;
    if (win === 'qtd') return Math.floor(m / 3) === currentQuarter && m <= currentMonthIdx;
    if (win === 'mtd') return m === currentMonthIdx;
    return false;
  };

  // Aggregate monthly figures matching the window. For MTD, we replace the
  // current month's monthly forecast with the Slack-based MTD revenue, so the
  // KPI matches the "current month pace" card below.
  const aggregate = (win) => {
    const acts = fyActuals.filter((a) => monthInWindow(a.date_iso, win));
    const fcs = fyForecastMonths.filter((m) => monthInWindow(m.date_iso, win));
    let revenue = acts.reduce((s, a) => s + a.revenue, 0) + fcs.reduce((s, m) => s + m.income.total, 0);
    let netIncome = acts.reduce((s, a) => s + a.net_income, 0) + fcs.reduce((s, m) => s + m.net_income, 0);
    if (win === 'mtd' && pulse?.today?.monthly_revenue_to_date != null) {
      const monthLabel = months[0]?.label;
      const alreadyClosed = acts.some((a) => a.month_label === monthLabel);
      if (!alreadyClosed) {
        revenue = pulse.today.monthly_revenue_to_date;
        const fcMargin = months[0]?.margin || 0;
        netIncome = revenue * fcMargin;
      }
    }
    const margin = revenue ? netIncome / revenue : 0;
    return { revenue, netIncome, margin, acts, fcs };
  };

  const agg = aggregate(period);
  const fyAgg = aggregate('fy');  // always needed for "% of BHAG" target reference

  // Cash for the selected window — end of the window's last month, falling back
  // to current ending cash when the window ends before the forecast starts.
  const lastWindowMonth = period === 'fy'
    ? months[months.length - 1]
    : period === 'ytd'
      ? months.slice().reverse().find((m) => new Date(m.date_iso).getUTCMonth() <= currentMonthIdx && new Date(m.date_iso).getUTCFullYear() === currentYear) || months[0]
      : period === 'qtd'
        ? months.slice().reverse().find((m) => {
            const d = new Date(m.date_iso);
            return d.getUTCFullYear() === currentYear && Math.floor(d.getUTCMonth() / 3) === currentQuarter && d.getUTCMonth() <= currentMonthIdx;
          }) || months[0]
        : months[0];  // mtd
  const endingCash = lastWindowMonth?.cash?.ending ?? f.totals.ending_cash;

  // BHAG % — for the selected window, against the prorated BHAG target.
  const proratedBhagTarget = bhag
    ? bhag.annual_revenue * (
        period === 'fy' ? 1
        : period === 'ytd' ? yearElapsedPct
        : period === 'qtd' ? (() => {
            const qStart = new Date(currentYear, currentQuarter * 3, 1);
            const qEnd = new Date(currentYear, currentQuarter * 3 + 3, 0);
            const qDays = (qEnd - qStart) / 86400000 + 1;
            const qElapsed = Math.min((today - qStart) / 86400000 + 1, qDays);
            return (qElapsed / qDays) * (qDays / fyDaysTotal);
          })()
        : monthPct / 12
      )
    : 0;
  const bhagPct = bhag ? agg.revenue / bhag.annual_revenue : 0;
  const bhagPaceDelta = bhag && proratedBhagTarget ? agg.revenue / proratedBhagTarget - 1 : null;

  // Labels for each window — used in sub-text and trend label.
  const WINDOW_META = {
    fy: { title: `FY ${currentYear}`, sub: `${fyActuals.length} actual + ${fyForecastMonths.length} forecast months`, trendLabel: 'MoM (latest)' },
    ytd: { title: 'YTD', sub: `Through ${today.toLocaleString('en-US', { month: 'short' })} ${today.getDate()}`, trendLabel: 'MoM' },
    qtd: { title: `Q${currentQuarter + 1} to date`, sub: `Quarter starts ${new Date(currentYear, currentQuarter * 3, 1).toLocaleString('en-US', { month: 'short' })}`, trendLabel: 'vs prior Q' },
    mtd: { title: 'Month-to-date', sub: `Day ${dayNum} of ${daysInMonth} · ${months[0]?.label}`, trendLabel: 'vs prior month' },
  };
  const meta = WINDOW_META[period];

  const cashData = months.map((m) => ({ label: m.label, cash: m.cash.ending }));
  const cashSafety = 200000;

  // --- Current month pace (Slack MTD vs stretch goal) ---
  const thisMonth = months[0];
  const mtd = pulse?.today?.monthly_revenue_to_date || 0;
  const stretchGoal = thisMonth.income.trademark;
  const projectedEoM = monthPct > 0 ? mtd / monthPct : 0;
  const vsStretch = projectedEoM - stretchGoal;

  // --- KPI trend + sparkline data ---
  // Trailing 6 months — same data regardless of window so trend context stays stable.
  const monthlyRevenueSeries = [
    ...fyActuals.map((a) => ({ label: a.month_label, v: a.revenue, isActual: true })),
    ...fyForecastMonths.map((m) => ({ label: m.label, v: m.income.total, isActual: false })),
  ].slice(-6);
  const monthlyNetSeries = [
    ...fyActuals.map((a) => ({ label: a.month_label, v: a.net_income })),
    ...fyForecastMonths.map((m) => ({ label: m.label, v: m.net_income })),
  ].slice(-6);
  const cashSeries = months.slice(0, 6).map((m) => ({ label: m.label, v: m.cash.ending }));

  // MoM delta — last two monthly points. For QTD window we compare to prior quarter.
  const periodDelta = (win) => {
    const revArr = [...fyActuals.map((a) => a.revenue), ...fyForecastMonths.map((m) => m.income.total)];
    const netArr = [...fyActuals.map((a) => a.net_income), ...fyForecastMonths.map((m) => m.net_income)];
    if (win === 'qtd') {
      // Compare current quarter cumulative to last quarter cumulative.
      const cqSlice = revArr.slice(currentQuarter * 3, currentQuarter * 3 + (currentMonthIdx - currentQuarter * 3 + 1));
      const lqSlice = revArr.slice((currentQuarter - 1) * 3, currentQuarter * 3);
      const cqSum = cqSlice.reduce((s, v) => s + v, 0);
      const lqSum = lqSlice.reduce((s, v) => s + v, 0);
      const cqNet = netArr.slice(currentQuarter * 3, currentQuarter * 3 + (currentMonthIdx - currentQuarter * 3 + 1)).reduce((s, v) => s + v, 0);
      const lqNet = netArr.slice((currentQuarter - 1) * 3, currentQuarter * 3).reduce((s, v) => s + v, 0);
      return {
        rev: lqSum ? (cqSum - lqSum) / Math.abs(lqSum) : null,
        net: lqNet ? (cqNet - lqNet) / Math.abs(lqNet) : null,
      };
    }
    // Default: MoM delta of the last two monthly points
    if (revArr.length < 2) return { rev: null, net: null };
    const r1 = revArr[revArr.length - 2], r2 = revArr[revArr.length - 1];
    const n1 = netArr[netArr.length - 2], n2 = netArr[netArr.length - 1];
    return {
      rev: r1 ? (r2 - r1) / Math.abs(r1) : null,
      net: n1 ? (n2 - n1) / Math.abs(n1) : null,
    };
  };
  const { rev: revDelta, net: netDelta } = periodDelta(period);

  // --- Mid-month milestone alerts ---
  // Use the ADJUSTED target for THIS calendar month (not annual/12), so
  // month-over-month goals are accurate when Joey's plan isn't linear.
  const currentMonthShortLabel = today.toLocaleString('en-US', { month: 'short' });
  const monthlyBhagTarget = bhagForLabel(currentMonthShortLabel) || (bhag ? bhag.annual_revenue / 12 : 0);
  const proratedMonthTarget = monthlyBhagTarget * monthPct;
  const mtdDelta = mtd - proratedMonthTarget;
  const mtdDeltaPct = proratedMonthTarget ? mtdDelta / proratedMonthTarget : 0;
  const nextMilestone = monthPct < 0.25 ? 25 : monthPct < 0.5 ? 50 : monthPct < 0.75 ? 75 : 100;
  const milestoneStatus = mtdDeltaPct >= 0.05 ? 'ahead' : mtdDeltaPct <= -0.05 ? 'behind' : 'on_track';

  const salesThisMonth = sales?.months?.find((m) => m.month === today.getMonth() + 1);
  const salesPaceDelta = salesThisMonth?.sales_goal
    ? (salesThisMonth.sales_actual || 0) / salesThisMonth.sales_goal - monthPct
    : null;

  return (
    <div className="space-y-12">
      <SectionHeader
        eyebrow="Dashboard"
        title="Where we are. Where we're going."
        description={`Every number on this screen is measured against the ${active.name} plan — the Keystone reports and budgets both use.`}
        right={
          <div className="no-print flex items-center gap-2">
            {user?.is_admin && (
              <button
                type="button"
                onClick={() => setReconcileOpen(true)}
                data-testid="dashboard-reconcile-btn"
                className="inline-flex items-center gap-2 border-2 border-indie-navy text-indie-navy font-semibold uppercase tracking-wider text-xs px-4 py-2 hover:bg-indie-navy hover:text-indie-yellow"
              >
                <RefreshCw size={14} /> Reconcile balances
              </button>
            )}
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="dashboard-print-btn"
              className="inline-flex items-center gap-2 bg-indie-yellow text-indie-ink font-semibold uppercase tracking-wider text-xs px-4 py-2 hover:bg-indie-yellow/85"
            >
              <Printer size={14} /> Download PDF
            </button>
          </div>
        }
      />

      {/* Period selector */}
      <div data-testid="dashboard-period-selector" className="no-print -mt-6 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wider">
        <span className="text-indie-ink/50">View:</span>
        {[
          { k: 'fy', label: `FY ${currentYear}` },
          { k: 'ytd', label: 'YTD' },
          { k: 'qtd', label: `Q${currentQuarter + 1} TD` },
          { k: 'mtd', label: 'MTD' },
        ].map((opt) => (
          <button
            key={opt.k}
            type="button"
            onClick={() => setPeriod(opt.k)}
            data-testid={`period-${opt.k}`}
            className={`px-3 py-1.5 font-semibold transition-colors ${
              period === opt.k
                ? 'bg-indie-navy text-indie-yellow border-2 border-indie-navy'
                : 'bg-white text-indie-navy border-2 border-indie-navy/20 hover:border-indie-navy'
            }`}
          >
            {opt.label}
          </button>
        ))}
        <span className="ml-auto text-indie-ink/50 normal-case tracking-normal text-xs">{meta.sub}</span>
      </div>

      {/* Annual plan hero — mirrors Sales Plan pattern (D1).
          Three tiles: Adjusted FY target (primary) · YTD Actual + attainment
          vs Original · Projected Year-End via BHAG trajectory. */}
      {(() => {
        const bhagMonths = bhag?.months || [];
        const annualAdjusted = bhagMonths.reduce((s, m) => s + (m.revenue_target || 0), 0) || null;
        const annualOriginal = bhagMonths.reduce((s, m) => s + (m.revenue_target_original || m.revenue_target || 0), 0) || annualAdjusted;
        const closed = bhagMonths.filter((m) => (m.revenue_actual || 0) > 0);
        const ytdActual = closed.reduce((s, m) => s + (m.revenue_actual || 0), 0);
        const ytdOriginalTarget = closed.reduce((s, m) => s + (m.revenue_target_original || m.revenue_target || 0), 0);
        const remainingAdjusted = bhagMonths.filter((m) => (m.revenue_actual || 0) === 0).reduce((s, m) => s + (m.revenue_target || 0), 0);
        const projectedEoY = ytdActual + remainingAdjusted;
        const attainmentPct = ytdOriginalTarget ? ytdActual / ytdOriginalTarget : null;
        const delta = annualAdjusted ? projectedEoY - annualAdjusted : 0;
        const onTrack = delta >= 0;
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dashboard-annual-hero">
            <div className="border-2 border-indie-navy bg-indie-navy text-indie-cream p-5" data-testid="dashboard-annual-target">
              <div className="flex items-center gap-2 flex-wrap">
                <ClockLabel type="cash" />
                <span className="text-[10px] uppercase tracking-[0.16em] text-indie-cream/60">Adjusted revenue target · {new Date().getFullYear()}</span>
              </div>
              <div className="mt-2 text-3xl font-semibold num-tabular">{moneyFull(annualAdjusted)}</div>
              <div className="mt-1 text-xs text-indie-cream/60">
                BHAG row 3 · includes planned paid-ad ramp
                {annualOriginal && annualOriginal !== annualAdjusted && <> · original was {moneyFull(annualOriginal)}</>}
              </div>
            </div>
            <div className="border-2 border-indie-ink/20 p-5" data-testid="dashboard-annual-ytd">
              <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
                YTD actual · attainment vs ORIGINAL
              </div>
              <div className="mt-2 text-3xl font-semibold text-indie-ink num-tabular">{moneyFull(ytdActual)}</div>
              <div className="mt-1 text-xs text-indie-ink/70">
                Original target through same window: {money(ytdOriginalTarget)}
                {attainmentPct != null && <> · <span className="font-semibold">{pct(attainmentPct, 0)}</span></>}
              </div>
              <div className="mt-1 text-[10px] text-indie-ink/50">
                Historical attainment uses BHAG row 2 (original), never adjusted.
              </div>
            </div>
            <div className={`border-2 p-5 ${onTrack ? 'border-emerald-600 bg-emerald-50' : 'border-rose-600 bg-rose-50'}`} data-testid="dashboard-annual-projected">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] font-semibold text-indie-ink/60">
                Projected year-end · BHAG trajectory
                {onTrack ? <TrendingUp size={12} className="text-emerald-700" /> : <TrendingDown size={12} className="text-rose-700" />}
              </div>
              <div className="mt-2 text-3xl font-semibold text-indie-ink num-tabular">{moneyFull(projectedEoY)}</div>
              {annualAdjusted && (
                <div className={`mt-1 text-xs font-semibold ${onTrack ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {onTrack ? '+' : ''}{money(delta)} vs adjusted target
                </div>
              )}
              <div className="mt-1 text-[10px] text-indie-ink/60">
                Future months land on BHAG&apos;s adjusted plan (not a flat run-rate).
              </div>
            </div>
          </div>
        );
      })()}

      {/* Mid-month milestone alerts */}
      <MidMonthMilestone
        monthLabel={thisMonth.label}
        dayNum={dayNum}
        daysInMonth={daysInMonth}
        monthPct={monthPct}
        nextMilestone={nextMilestone}
        mtd={mtd}
        proratedTarget={proratedMonthTarget}
        mtdDelta={mtdDelta}
        mtdDeltaPct={mtdDeltaPct}
        milestoneStatus={milestoneStatus}
        salesPaceDelta={salesPaceDelta}
        salesThisMonth={salesThisMonth}
      />

      {/* Forecast chart (D2) — mirrors Sales Plan grammar. Solid teal Actual
          for closed months, solid blue Adjusted plan (past actuals + future
          adjusted target), dashed navy Original plan as attainment baseline. */}
      <div className="bg-white border-t-4 border-indie-navy p-6" data-testid="dashboard-forecast-chart">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Target size={14} className="text-indie-ink/70" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">Revenue · {currentYear} · plan vs actual</div>
            <ClockLabel type="cash" />
          </div>
          <DataSourceBadge
            source="BHAG · Adjusted (row 3) + Original (row 2) + QBO actuals"
            asOf={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            confidence="green"
            testId="forecast-source-badge"
          />
        </div>
        <div className="text-[11px] text-indie-ink/60 mb-2">
          Adjusted plan (blue, primary) is BHAG row 3 — the active goal with planned paid-ad increases baked in.
          Original plan (dashed navy) is BHAG row 2 — the baseline set at the start of the year, used for historical attainment.
          Actual (teal) stops at the last completed month.
        </div>
        <div className="h-80">
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ left: 20, right: 20, top: 20, bottom: 10 }}>
              <CartesianGrid {...CHART_GRID_STROKE} />
              <XAxis dataKey="label" stroke="#101820" fontSize={11} tickLine={false} axisLine={CHART_AXIS_LINE} />
              <YAxis stroke="#101820" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_TOOLTIP_LABEL} itemStyle={CHART_TOOLTIP_ITEM} formatter={(v) => v == null ? '—' : moneyFull(v)} labelFormatter={(l) => `${l} ${currentYear}`} />
              <Legend iconType="rect" wrapperStyle={CHART_LEGEND_WRAPPER} />
              <Line type="monotone" dataKey="adjusted" name="Adjusted plan (primary · past actuals + future adjusted)" stroke="#1D57A5" strokeWidth={3} dot={{ r: 3 }} connectNulls />
              <Line type="monotone" dataKey="original" name="Original plan (attainment baseline)" stroke="#141B4D" strokeWidth={2} strokeDasharray="6 4" dot={false} connectNulls />
              <Line type="monotone" dataKey="actual" name="Actual (closed months)" stroke="#0F766E" strokeWidth={3} dot={{ r: 5 }} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Day-by-day MTD line chart (D3) — replaces the static "Current month" navy card.
          Cumulative actual vs prior-month same-DoM for reference.
          `series[]` from /sales/console/mtd. */}
      {mtdSeries?.series?.length > 0 && (() => {
        const priorFinal = mtdSeries.prior_month_final;
        const lookbackFinal = mtdSeries.lookback_final;
        const lookbackLabel = mtdSeries.lookback_month_label;
        const lookbackMonthsBack = mtdSeries.lookback_months_back;
        const lookbackHasDaily = mtdSeries.lookback_has_daily;
        const priorDelta = priorFinal ? (mtd - priorFinal) / priorFinal : null;
        const lookbackDelta = lookbackFinal ? (mtd - lookbackFinal) / lookbackFinal : null;
        const lookbackCardLabel = lookbackMonthsBack ? `vs ${lookbackMonthsBack} months ago · ${lookbackLabel}` : null;
        return (
        <div className="bg-white border-t-4 border-indie-navy p-6" data-testid="dashboard-mtd-chart">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <TrendingUp size={14} className="text-indie-ink/70" strokeWidth={1.5} />
              <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
                {mtdSeries.month_label || thisMonth.label} · day-by-day cumulative revenue
              </div>
              <ClockLabel type="cash" />
            </div>
            <div className="text-[11px] text-indie-ink/60 num-tabular">
              Day {mtdSeries.latest?.day || dayNum} of {daysInMonth} · <span className="font-semibold text-indie-ink">{moneyFull(mtd)}</span> MTD
            </div>
          </div>

          {/* Comparison strip — MTD vs prior month vs adaptive lookback
              (6 mo if daily data available, else 3 mo). Both references are
              month-final totals; deltas compare current MTD honestly. */}
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="dashboard-mtd-comparison-strip">
            <div className="border border-indie-navy/20 px-3 py-2">
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">MTD · {mtdSeries.month_label}</div>
              <div className="text-lg font-semibold text-indie-navy num-tabular">{moneyFull(mtd)}</div>
              <div className="text-[10px] text-indie-ink/60">Day {mtdSeries.latest?.day || dayNum} of {daysInMonth}</div>
            </div>
            <div className="border-l-4 border-indie-neg-dark/60 border-t border-r border-b border-indie-ink/10 px-3 py-2 bg-indie-cream/40" data-testid="dashboard-mtd-vs-prior">
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">vs prior month · {mtdSeries.prior_month_label}</div>
              <div className="text-lg font-semibold text-indie-navy num-tabular">
                {priorFinal != null ? moneyFull(priorFinal) : '—'}
                {priorDelta != null && (
                  <span className={`ml-2 text-xs ${priorDelta >= 0 ? 'text-indie-pos-dark' : 'text-indie-neg-dark'}`}>
                    {priorDelta >= 0 ? '+' : ''}{(priorDelta * 100).toFixed(1)}%
                  </span>
                )}
              </div>
              <div className="text-[10px] text-indie-ink/60">Month final · cash clock</div>
            </div>
            {lookbackFinal != null && (
              <div className="border-l-4 border-indie-cobalt/70 border-t border-r border-b border-indie-ink/10 px-3 py-2 bg-indie-cream/40" data-testid="dashboard-mtd-vs-lookback">
                <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">{lookbackCardLabel}</div>
                <div className="text-lg font-semibold text-indie-navy num-tabular">
                  {moneyFull(lookbackFinal)}
                  {lookbackDelta != null && (
                    <span className={`ml-2 text-xs ${lookbackDelta >= 0 ? 'text-indie-pos-dark' : 'text-indie-neg-dark'}`}>
                      {lookbackDelta >= 0 ? '+' : ''}{(lookbackDelta * 100).toFixed(1)}%
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-indie-ink/60">
                  {lookbackHasDaily ? 'Day-by-day overlaid on chart' : 'Month final only — daily curve unavailable'}
                  {lookbackMonthsBack === 3 && ' · 6-mo lookback pending Feb 2026 backfill'}
                </div>
              </div>
            )}
          </div>

          <div className="h-64 mt-3">
            <ResponsiveContainer>
              <LineChart data={mtdSeries.series} margin={CHART_MARGIN}>
                <CartesianGrid {...CHART_GRID_STROKE} />
                <XAxis dataKey="day" stroke="#101820" fontSize={11} tickLine={false} axisLine={CHART_AXIS_LINE} />
                <YAxis stroke="#101820" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_TOOLTIP_LABEL} itemStyle={CHART_TOOLTIP_ITEM} formatter={(v) => v == null ? '—' : moneyFull(v)} labelFormatter={(l) => `Day ${l}`} />
                <Legend wrapperStyle={CHART_LEGEND_WRAPPER} />
                <Line type="monotone" dataKey="mtd_revenue" name={`${mtdSeries.month_label || 'This month'} · cumulative`} stroke="#141B4D" strokeWidth={3} dot={{ r: 3 }} connectNulls />
                <Line type="monotone" dataKey="prior" name={`${mtdSeries.prior_month_label || 'Prior month'} · same day`} stroke="#B4530C" strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls />
                {lookbackHasDaily && (
                  <Line type="monotone" dataKey="lookback" name={`${lookbackLabel || 'Lookback'} · same day (${lookbackMonthsBack} mo ago)`} stroke="#1D57A5" strokeWidth={2} strokeDasharray="2 4" dot={false} connectNulls />
                )}
                {mtdSeries.baseline_target != null && (
                  <ReferenceLine y={mtdSeries.baseline_target} stroke="#006747" strokeDasharray="5 4" strokeOpacity={0.8} label={{ value: `Baseline ${moneyFull(mtdSeries.baseline_target)}`, position: 'insideTopRight', fontSize: 10, fill: '#006747' }} />
                )}
                {!lookbackHasDaily && lookbackFinal != null && (
                  <ReferenceLine y={lookbackFinal} stroke="#1D57A5" strokeDasharray="2 4" strokeOpacity={0.8} label={{ value: `${lookbackMonthsBack} mo ago · ${lookbackLabel} final ${moneyFull(lookbackFinal)}`, position: 'insideBottomLeft', fontSize: 10, fill: '#1D57A5' }} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 text-[10px] text-indie-ink/50">
            On pace to end the month at {moneyFull(projectedEoM)} · stretch goal {moneyFull(stretchGoal)}. Source: Slack #dailyhighlight + BHAG monthly targets.
            {lookbackLabel && (
              <> Comparison reference: {lookbackLabel} ({lookbackMonthsBack} mo ago{lookbackHasDaily ? ', day-by-day' : `, final only $${Math.round((lookbackFinal || 0) / 1000)}k`}). History starts Jan 2026 — 6-mo daily curve populates as backfill completes; YoY overlay unlocks Jan 2027.</>
            )}
          </div>
        </div>
        );
      })()}

      {/* Cash runway — advisor-recommended reserve visibility */}
      <CashRunwayCard key={`crw-${reconcileTick}`} />

      {/* IOLTA trust balance — working reconciliation view */}
      <IoltaCard key={`iolta-${reconcileTick}`} />

      {reconcileOpen && (
        <ReconcileBalancesModal
          onClose={() => setReconcileOpen(false)}
          onSaved={() => setReconcileTick((t) => t + 1)}
        />
      )}

      {/* Quarterly table + Cash runway */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border-t-4 border-indie-navy p-6">
          <div className="label-editorial">Quarterly roll-up · {currentYear}</div>
          <div className="text-xl font-semibold text-indie-navy mt-1 mb-4">Q1 – Q4 by status</div>
          <table data-testid="quarterly-table" className="w-full text-sm num-tabular">
            <thead>
              <tr className="bg-indie-navy text-indie-cream">
                <th className="text-left px-3 py-2 font-medium tracking-wider uppercase text-xs">Q</th>
                <th className="text-left px-3 py-2 font-medium tracking-wider uppercase text-xs">Status</th>
                <th className="text-right px-3 py-2 font-medium tracking-wider uppercase text-xs">Revenue</th>
                <th className="text-right px-3 py-2 font-medium tracking-wider uppercase text-xs">Expenses</th>
                <th className="text-right px-3 py-2 font-medium tracking-wider uppercase text-xs">Net</th>
                <th className="text-right px-3 py-2 font-medium tracking-wider uppercase text-xs">Margin</th>
              </tr>
            </thead>
            <tbody>
              {calendarQuarterly.map((q, i) => {
                const qNum = i + 1;
                let status, statusCls;
                if (qNum < currentQuarter + 1) { status = 'Actual'; statusCls = 'text-indie-evergreen bg-indie-evergreen/[0.08]'; }
                else if (qNum === currentQuarter + 1) { status = 'YTD'; statusCls = 'text-indie-cobalt bg-indie-cobalt/[0.08]'; }
                else { status = 'Forecast'; statusCls = 'text-indie-ink/60 bg-indie-cream'; }
                return (
                  <tr key={q.label} className={i % 2 === 0 ? 'bg-white' : 'bg-indie-cream'}>
                    <td className="px-3 py-2.5 font-medium">{q.label}</td>
                    <td className="px-3 py-2.5">
                      <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${statusCls}`}>{status}</span>
                    </td>
                    <td className="text-right px-3 py-2.5">{moneyFull(q.revenue)}</td>
                    <td className="text-right px-3 py-2.5">{moneyFull(q.expenses)}</td>
                    <td className="text-right px-3 py-2.5 font-medium">{moneyFull(q.net_income)}</td>
                    <td className="text-right px-3 py-2.5 text-indie-evergreen">{pct(q.margin)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-4 pt-3 border-t border-indie-navy/10 text-[11px] text-indie-ink/50 leading-relaxed">
            <span className="text-indie-evergreen font-semibold">Actual</span> = closed quarter · <span className="text-indie-cobalt font-semibold">YTD</span> = current quarter in progress · <span className="text-indie-ink/70 font-semibold">Forecast</span> = future. Numbers computed from <span className="font-semibold">{active.name}</span> assumptions — change any driver in Assumptions to watch these update live.
          </div>
        </div>

        <div className="bg-white border-t-4 border-indie-navy p-6">
          <div className="label-editorial">Cash trajectory</div>
          <div className="text-xl font-semibold text-indie-navy mt-1 mb-4">12-month forecast</div>
          <div className="h-60">
            <ResponsiveContainer>
              <LineChart data={cashData} margin={CHART_MARGIN}>
                <CartesianGrid {...CHART_GRID_STROKE} />
                <XAxis dataKey="label" stroke="#101820" fontSize={11} tickLine={false} axisLine={CHART_AXIS_LINE} />
                <YAxis stroke="#101820" fontSize={11} tickLine={false} axisLine={false} tickFormatter={money} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_TOOLTIP_LABEL} itemStyle={CHART_TOOLTIP_ITEM} formatter={moneyFull} />
                <ReferenceLine y={cashSafety} stroke="#141B4D" strokeDasharray="4 4" label={SAFETY_FLOOR_LABEL} />
                <Line type="monotone" dataKey="cash" name="Ending cash" stroke="#1D57A5" strokeWidth={2.5} dot={CHART_DOT_COBALT} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 pt-3 border-t border-indie-navy/10 text-[11px] text-indie-ink/50 leading-relaxed">
            Starting cash + monthly net income – owner draws, rolled forward. Safety floor set to $200K — adjust in Assumptions.
          </div>
        </div>
      </div>

      <Callout title="A note on these numbers" tone="default">
        Every driver on the Assumptions page feeds this forecast. Change one value and the whole dashboard updates —
        no spreadsheet gymnastics. Default scenarios are seeded from your March 2026 forecast; edit them or clone your own.
      </Callout>
    </div>
  );
}

// Mid-month milestone alerts — visual signal for whether MTD is keeping up with
// a prorated monthly BHAG target. Splits the month into 4 buckets (25/50/75/100%)
// so the team gets a clear "course-correct now" signal before month-end.
function MidMonthMilestone({
  monthLabel, dayNum, daysInMonth, monthPct, nextMilestone,
  mtd, proratedTarget, mtdDelta, mtdDeltaPct, milestoneStatus,
  salesPaceDelta, salesThisMonth,
}) {
  const palette = {
    ahead: { icon: CheckCircle2, color: 'text-indie-evergreen', bg: 'bg-indie-evergreen/[0.08]', border: 'border-indie-evergreen', label: 'AHEAD OF PACE' },
    on_track: { icon: Clock, color: 'text-indie-cobalt', bg: 'bg-indie-cobalt/[0.06]', border: 'border-indie-cobalt', label: 'ON TRACK' },
    behind: { icon: AlertTriangle, color: 'text-indie-neg-dark', bg: 'bg-indie-neg-dark/[0.06]', border: 'border-indie-neg-dark', label: 'BEHIND PACE' },
  }[milestoneStatus];
  const Icon = palette.icon;

  return (
    <div data-testid="mid-month-milestone" className={`bg-white border-t-4 ${palette.border} p-6`}>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="label-editorial">Mid-month milestone · {monthLabel}</div>
          <div className="text-xl font-semibold text-indie-navy mt-1">Course-correct signal</div>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 ${palette.bg} ${palette.color}`}>
          <Icon size={16} />
          <span className="text-xs font-semibold uppercase tracking-wider">{palette.label}</span>
        </div>
      </div>

      {/* Milestone progress bar */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-1.5 text-[11px] uppercase tracking-wider text-indie-ink/60">
          <span>Day {dayNum} of {daysInMonth}</span>
          <span className="num-tabular">{Math.round(monthPct * 100)}% elapsed</span>
        </div>
        <div className="relative h-2 bg-indie-cream">
          <div className="absolute top-0 left-0 h-2 bg-indie-navy" style={{ width: `${Math.min(monthPct * 100, 100)}%` }} />
          {[25, 50, 75].map((m) => (
            <div key={m} className="absolute top-0 h-2 w-px bg-indie-ink/40" style={{ left: `${m}%` }} />
          ))}
        </div>
        <div className="flex justify-between mt-1 text-[10px] text-indie-ink/40 tracking-wider">
          <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div>
          <div className="label-editorial text-indie-ink/60">Revenue MTD</div>
          <div className="text-2xl font-semibold text-indie-navy num-tabular mt-1">{moneyFull(mtd)}</div>
          <div className="text-xs text-indie-ink/60 mt-1 num-tabular">vs. {moneyFull(proratedTarget)} prorated target</div>
        </div>
        <div>
          <div className="label-editorial text-indie-ink/60">Delta vs. pace</div>
          <div className={`text-2xl font-semibold num-tabular mt-1 ${mtdDelta >= 0 ? 'text-indie-evergreen' : 'text-indie-neg-dark'}`}>
            {mtdDelta >= 0 ? '+' : ''}{moneyFull(mtdDelta)}
          </div>
          <div className={`text-xs mt-1 num-tabular ${mtdDelta >= 0 ? 'text-indie-evergreen' : 'text-indie-neg-dark'}`}>
            {mtdDelta >= 0 ? '+' : ''}{(mtdDeltaPct * 100).toFixed(1)}% of prorated target
          </div>
        </div>
        <div>
          <div className="label-editorial text-indie-ink/60">Sales pace</div>
          {salesPaceDelta != null ? (
            <>
              <div className={`text-2xl font-semibold num-tabular mt-1 ${salesPaceDelta >= 0 ? 'text-indie-evergreen' : 'text-indie-neg-dark'}`}>
                {salesPaceDelta >= 0 ? '+' : ''}{(salesPaceDelta * 100).toFixed(1)}pp
              </div>
              <div className="text-xs text-indie-ink/60 mt-1 num-tabular">
                {salesThisMonth?.sales_actual || 0} of {salesThisMonth?.sales_goal || 0} sales · {Math.round(monthPct * 100)}% of month
              </div>
            </>
          ) : (
            <>
              <div className="text-2xl font-semibold text-indie-ink/40 mt-1">—</div>
              <div className="text-xs text-indie-ink/50 mt-1">Sync the Sales sheet to enable this</div>
            </>
          )}
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-indie-navy/10 text-xs text-indie-ink/60 leading-relaxed">
        {milestoneStatus === 'behind' && (
          <>Revenue is trailing the prorated BHAG target with <span className="font-semibold">{Math.round(monthPct * 100)}% of {monthLabel} elapsed</span> (next checkpoint: {nextMilestone}%). Consider a Slack push to sales + ad spend audit before month-end.</>
        )}
        {milestoneStatus === 'on_track' && (
          <>Revenue is tracking within 5% of the prorated BHAG target with <span className="font-semibold">{Math.round(monthPct * 100)}% of {monthLabel} elapsed</span> (next checkpoint: {nextMilestone}%). Stay the course.</>
        )}
        {milestoneStatus === 'ahead' && (
          <>Revenue is running ahead of the prorated BHAG target with <span className="font-semibold">{Math.round(monthPct * 100)}% of {monthLabel} elapsed</span> (next checkpoint: {nextMilestone}%). Don&apos;t let up — the easier the second half, the bigger the cushion.</>
        )}
      </div>
    </div>
  );
}
